<template>
  <div>
    <h1>Privacy Policy</h1>

    <section class="legal-section">
      <h2>1. Privacy at a glance</h2>
      <h3>General information</h3>
      <p>The following notes provide a simple overview of what happens to your personal data when you visit our website or use our services. Personal data is any data that can be used to identify you personally.</p>
      <h3>Data collection on our website</h3>
      <p><strong>Who is responsible for data collection on this website?</strong></p>
      <p>Data processing on this website is carried out by the website operator. You can find their contact details in the section "Information on the responsible party" in this privacy policy.</p>
      <p><strong>How do we collect your data?</strong></p>
      <p>Your data is collected when you provide it to us, for example when you enter data in our AI automations (date of birth, place of birth, time of birth for horoscope creation).</p>
      <p><strong>What do we use your data for?</strong></p>
      <p>Some data is collected to ensure error-free provision of the website. Other data may be used to analyze your user behavior or to create personalized horoscopes and spiritual consulting content.</p>
    </section>

    <section class="legal-section">
      <h2>2. Hosting</h2>
      <p>We host our website via <strong>Vercel Inc.</strong>, 440 N Barranca Ave #4133, Covina, CA 91723, USA. The website is delivered through a global CDN; data may be processed in the EU and the USA.</p>
      <p><strong>Legal basis:</strong> Art. 6(1)(f) GDPR (legitimate interest in secure and efficient provision). For transfers to the USA, we rely on the EU Commission's Standard Contractual Clauses.</p>
    </section>

    <section class="legal-section">
      <h2>3. Cookies and local storage</h2>
      <p>This website uses technically necessary local browser storage (localStorage) for theme settings and your cookie consent.</p>
      <p>Analytics tracking (SupportSrc / SP Pixel) is only activated after your consent via our cookie banner. Live chat (Pulse.is) and newsletter forms (SendPulse) are also loaded only after consent in the "Communication & forms" category.</p>
      <p>You can revoke consent at any time by clearing website data in your browser or reopening the cookie settings.</p>
      <p><strong>Legal basis:</strong> Art. 6(1)(a) GDPR (consent) for analytics and communication tools; Art. 6(1)(f) GDPR for technically necessary storage.</p>
    </section>

    <section class="legal-section">
      <h2>4. General notes and mandatory information</h2>
      <h3>Data protection</h3>
      <p>The operators of these pages take the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with statutory data protection regulations and this privacy policy.</p>
      <h3>Information on the responsible party</h3>
      <p>The party responsible for data processing on this website is:</p>
      <p>
        Personal Coding LLC<br>
        Authorized representative: Dawid Paliczuk<br>
        2125 Biscayne Blvd, Ste 204 #25212<br>
        33137 Miami, Florida, USA<br>
        Florida Division of Corporations, Doc. No. L26000122441
      </p>
      <p>Data protection contact: Dawid Paliczuk (currently working internationally; contact via the channels below only).</p>
      <p>
        Phone: +49 176 22291967<br>
        Email: dawid@personalcoding.de
      </p>
      <h3>Representative in the Union (Art. 27 GDPR)</h3>
      <p>Personal Coding LLC is not established in the EU but processes personal data of visitors and customers from the EU. Data subjects in the EU may contact us directly at dawid@personalcoding.de. We are assessing the appointment of a representative in the Union pursuant to Art. 27 GDPR and will publish contact details here once appointed.</p>
      <h3>Storage period</h3>
      <p>Unless a more specific storage period is stated in this privacy policy, your personal data will remain with us until the purpose for data processing no longer applies. If you request deletion or revoke consent, your data will be deleted unless we have other legally permissible reasons to store it.</p>
    </section>

    <section class="legal-section">
      <h2>5. Data collection through AI automations</h2>
      <h3>Instagram automation for horoscope creation</h3>
      <p>We offer an automated solution via Instagram that creates personalized horoscopes. We process the following data:</p>
      <h4>Type of data:</h4>
      <ul>
        <li>Date of birth</li>
        <li>Place of birth</li>
        <li>Time of birth</li>
        <li>Instagram username (for communication)</li>
      </ul>
      <h4>Purpose of processing:</h4>
      <ul>
        <li>Creation of personalized horoscopes (text and image)</li>
        <li>Provision of spiritual consulting content</li>
        <li>Communication via Instagram Direct Messages</li>
      </ul>
      <h4>Legal basis:</h4>
      <p>Processing is based on your consent (Art. 6(1)(a) GDPR), which you provide by voluntarily entering your data in the automation.</p>
      <h4>Storage location and duration:</h4>
      <p>Your data is stored with our processors, including in the EU and the USA. Automatic deletion does not take place. You can request deletion at any time at dawid@personalcoding.de.</p>
    </section>

    <section class="legal-section">
      <h2>6. External service providers</h2>
      <h3>Pulse.is (live chat)</h3>
      <p>We use Pulse.is for live chat on this website. The chat widget is loaded only after your consent in the cookie category "Communication & forms". Technical usage data and chat content may be processed when you use the chat.</p>
      <p><strong>Provider:</strong> Pulse.is (CDN: cdn.pulse.is)</p>
      <p><strong>Legal basis:</strong> Art. 6(1)(a) GDPR (consent via cookie banner).</p>
      <h3>Reclaim.ai (appointment booking)</h3>
      <p>For booking consultations, we link to Reclaim.ai. When you click a booking link, you are redirected to Reclaim.ai's website, where personal data (e.g. name, email, appointment request) may be processed.</p>
      <p><strong>Provider:</strong> Reclaim.ai, Inc., USA</p>
      <p><strong>Legal basis:</strong> Art. 6(1)(b) GDPR (contract performance or pre-contractual measures) if you book an appointment; otherwise Art. 6(1)(f) GDPR (legitimate interest in efficient scheduling).</p>
      <p>Further information: <a href="https://reclaim.ai/privacy" target="_blank" rel="noopener">https://reclaim.ai/privacy</a></p>
      <h3>SendPulse (automation platform & newsletter)</h3>
      <p>We use SendPulse for newsletter signups and to provide our automation services. The newsletter form script is loaded only after your consent in the cookie category "Communication & forms".</p>
      <p><strong>Provider:</strong> SendPulse LLC, 220 E 23rd St #401, New York, NY 10010, USA</p>
      <h4>Transferred data:</h4>
      <ul>
        <li>Name and email address (newsletter signup)</li>
        <li>Your inputs in automation services (birth data, etc.)</li>
        <li>Instagram communication data</li>
      </ul>
      <p>Newsletter: Art. 6(1)(a) GDPR (consent). Automation services: Art. 6(1)(b) GDPR (contract performance). For transfers to the USA, we rely on the EU Commission's Standard Contractual Clauses. The data processing agreement (DPA) with SendPulse is part of SendPulse's terms of service and applies when you register or use the account: <a href="https://sendpulse.com/de/legal/processing" target="_blank" rel="noopener">sendpulse.com/de/legal/processing</a></p>
      <h3>Instagram Business API (Meta)</h3>
      <p>We use Meta's Instagram Business API for communication via Instagram Direct Messages. Instagram serves solely as a communication channel.</p>
      <h4>Processed data:</h4>
      <ul>
        <li>Instagram username</li>
        <li>Message metadata (timestamps, etc.)</li>
      </ul>
      <p>Further information can be found in Meta's privacy policy: <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener">https://www.facebook.com/privacy/policy</a></p>
    </section>

    <section class="legal-section">
      <h2>7. Newsletter and marketing</h2>
      <h3>Newsletter subscription (optional)</h3>
      <p>At your explicit request, we may add you to newsletter systems. This only happens with your explicit consent.</p>
      <h4>Processed data:</h4>
      <ul>
        <li>Email address</li>
        <li>Name (if provided)</li>
        <li>Areas of interest (based on automation interactions)</li>
      </ul>
      <h4>Legal basis:</h4>
      <p>Art. 6(1)(a) GDPR (consent). You can revoke your consent at any time.</p>
      <h3>Measurement of usage behavior (website tracking)</h3>
      <p>To optimize our website and marketing measures, we record events related to usage behavior. We use a tracking pixel (SupportSrc / SP Pixel) for this purpose.</p>
      <h4>Recorded events (examples):</h4>
      <ul>
        <li>Clicks on central call-to-action elements (e.g. appointment booking)</li>
        <li>Interactions with forms (e.g. newsletter signup)</li>
        <li>Use of individual features (e.g. quiz interactions, test buttons)</li>
        <li>Time spent on the website (in seconds)</li>
        <li>Clicks on legal links (e.g. imprint)</li>
      </ul>
      <h4>Processed data:</h4>
      <ul>
        <li>Technical usage data (time, page visited, event type)</li>
        <li>Pseudonymous event parameters for usage analysis</li>
        <li>Email address for newsletter-related events</li>
      </ul>
      <h4>Purpose:</h4>
      <p>Analysis and improvement of the website, measurement of content and marketing effectiveness, and optimization of user guidance.</p>
      <h4>Legal basis:</h4>
      <p>Processing takes place exclusively on the basis of your consent pursuant to Art. 6(1)(a) GDPR, which you provide via our cookie banner. Without consent, no tracking occurs.</p>
    </section>

    <section class="legal-section">
      <h2>8. Data sharing</h2>
      <h3>Sharing with coaches and partners</h3>
      <p>In certain cases, your horoscope data may be shared with qualified coaches or astrologers, exclusively:</p>
      <ul>
        <li>With your explicit prior consent</li>
        <li>For in-depth spiritual consulting</li>
        <li>To improve the quality of our services</li>
      </ul>
      <h3>Studies and research</h3>
      <p>Anonymized data may be used for statistical evaluations and studies. Identification of your person is not possible. This only occurs with your separate consent.</p>
    </section>

    <section class="legal-section">
      <h2>9. Your rights</h2>
      <p>You have the right at any time to:</p>
      <ul>
        <li><strong>Access</strong> your personal data stored by us (Art. 15 GDPR)</li>
        <li><strong>Rectification</strong> of incorrect personal data (Art. 16 GDPR)</li>
        <li><strong>Erasure</strong> of your personal data (Art. 17 GDPR)</li>
        <li><strong>Restriction</strong> of processing (Art. 18 GDPR)</li>
        <li><strong>Object</strong> to processing (Art. 21 GDPR)</li>
        <li><strong>Data portability</strong> (Art. 20 GDPR)</li>
      </ul>
      <h3>Revocation of consent</h3>
      <p>Many processing operations are only possible with your explicit consent. You can revoke consent at any time. The lawfulness of processing before revocation remains unaffected.</p>
      <h3>Deletion of your data</h3>
      <p><strong>Newsletter:</strong> Unsubscribe via the link in every newsletter email or email dawid@personalcoding.de with subject "Unsubscribe newsletter".</p>
      <p><strong>All other data:</strong> Send deletion requests to dawid@personalcoding.de with subject "Privacy – deletion". We typically process valid requests within 30 days.</p>
      <h3>Right to lodge a complaint</h3>
      <p>In the event of GDPR violations, you have the right to lodge a complaint with a supervisory authority, in particular in the member state of your habitual residence, place of work, or place of the alleged infringement.</p>
      <p>An overview of EU data protection authorities is available from the European Data Protection Board: <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener">edpb.europa.eu</a></p>
    </section>

    <section class="legal-section">
      <h2>10. EU AI Act</h2>
      <p>From 2 August 2026, transparency obligations under Article 50 of Regulation (EU) 2024/1689 (EU AI Act) apply to certain AI systems.</p>
      <p>When you interact with our live chat on this website, you are informed before first use that you are interacting with an AI-supported system. Chat automations (e.g. Instagram, Telegram, or WhatsApp bots) and AI-generated content (e.g. horoscope text or images) are labelled accordingly.</p>
      <p>Detailed information on AI systems used, your role as deployer in B2B projects, and synthetic content is available on our <a href="/ki-transparenz">AI transparency page</a>.</p>
    </section>

    <section class="legal-section">
      <h2>11. Contact</h2>
      <p>For questions about data protection, exercising your rights, or complaints, please contact:</p>
      <p>
        <strong>Personal Coding LLC - Dawid Paliczuk</strong><br>
        Email: dawid@personalcoding.de<br>
        Phone: +49 176 22291967
      </p>
    </section>

    <section class="legal-section">
      <h2>12. Changes to this privacy policy</h2>
      <p>We reserve the right to adapt this privacy policy so that it always meets current legal requirements or reflects changes to our services. The new privacy policy applies on your next visit.</p>
      <p><strong>Status:</strong> July 2026</p>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PrivacyEn'
}
</script>
